import React from "react";
import { motion } from "framer-motion";

/**
 * Page d'accueil — Data Analytics · AI · Web Development
 * À remplacer dans src/Components/Landing.jsx
 * Compatible avec React 18, Tailwind CSS 3 et framer-motion du projet.
 * Aucune image, aucun logo et aucune nouvelle dépendance.
 */

const expertise = [
  {
    number: "01",
    title: "Data Analytics & BI",
    description: "Transforming data into clear, actionable business insights.",
    technologies: [],
    icon: "analytics",
    color: "#B9A2FF",
  },
  {
    number: "02",
    title: "Artificial Intelligence",
    description: "Building intelligent solutions with AI and automation.",
    technologies: [],
    icon: "ai",
    color: "#99C4FF",
  },
  {
    number: "03",
    title: "Web Development",
    description: "Creating responsive applications with intuitive experiences.",
    technologies: [],
    icon: "web",
    color: "#C9A3E8",
  },
];

function ArrowIcon() {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14m-6-6 6 6-6 6" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12m-4-4 4 4 4-4M5 17v4h14v-4" />
    </svg>
  );
}

function DomainIcon({ type }) {
  if (type === "analytics") {
    return (
      <svg aria-hidden="true" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 20V5m0 15h16M8 15v-4m5 4V7m5 8v-7" />
      </svg>
    );
  }
  if (type === "ai") {
    return (
      <svg aria-hidden="true" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" /><path d="M12 3v6m0 6v6M3 12h6m6 0h6M5.7 5.7 9.9 9.9m4.2 4.2 4.2 4.2m0-12.6-4.2 4.2m-4.2 4.2-4.2 4.2" />
      </svg>
    );
  }
  return (
    <svg aria-hidden="true" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18m-11 3-3 3 3 3m4-6 3 3-3 3" />
    </svg>
  );
}

export default function Landing() {
  return (
    <section
      id="home"
      aria-labelledby="hero-title"
      className="relative isolate w-full min-h-[100svh] overflow-hidden bg-[#0B0B11] text-white"
    >
      {/* Ambiance discrète : fond sombre, halos violets, points très subtils */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-[190px] -top-[110px] h-[510px] w-[510px] rounded-full bg-[#7950F2]/[0.19] blur-[125px]" />
        <div className="absolute -right-[140px] top-[100px] h-[470px] w-[470px] rounded-full bg-[#895389]/[0.17] blur-[120px]" />
        <div className="absolute inset-0 opacity-[0.20] [background-image:radial-gradient(#77708E_0.7px,transparent_0.7px)] [background-size:28px_28px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0B0B11]/10 to-[#0B0B11]/70" />
      </div>

      <div className="relative mx-auto flex min-h-[100svh] w-full max-w-[1380px] items-center px-[24px] pb-[90px] pt-[145px] sm:px-[40px] min-[1100px]:px-[72px] min-[1100px]:pb-[110px]">
        <div className="grid w-full items-center gap-[52px] min-[960px]:grid-cols-[minmax(0,1.14fr)_minmax(340px,0.86fr)] min-[960px]:gap-[64px]">

          {/* Partie gauche : un positionnement clair et une lecture immédiate */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="min-w-0"
          >
            <div className="mb-[25px] inline-flex flex-wrap items-center gap-[10px] rounded-full border border-[#B79CF8]/[0.27] bg-[#7950F2]/[0.09] px-[15px] py-[9px]">
              <span className="h-[7px] w-[7px] rounded-full bg-[#B69BFF]" />
              <span className="text-[11px] font-bold uppercase tracking-[1.5px] text-[#E1D4FF]">
                Data · Intelligence · Digital products
              </span>
            </div>

            <p className="mb-[8px] text-[14px] font-semibold tracking-[0.8px] text-[#CFC3E9]">Hello, I'm</p>
            <h1
              id="hero-title"
              className="text-[clamp(46px,5.3vw,76px)] font-extrabold leading-[1.08] tracking-[-2px] text-white"
            >
              Salma Inane<span className="text-[#A98BFF]">.</span>
            </h1>

            {/* Pas d'effet typing : les trois domaines sont visibles immédiatement */}
            <h2 className="mt-[18px] max-w-[650px] text-[clamp(23px,2.7vw,34px)] font-bold leading-[1.3] tracking-[-0.6px] text-[#EDE6FF]">
              Data Analytics <span className="text-[#9474EB]">·</span> AI <span className="text-[#9474EB]">·</span> Web Development
            </h2>

            <p className="mt-[25px] max-w-[630px] text-[16px] leading-[1.85] text-[#C9C6D4] sm:text-[17px]">
              State Engineer in Computer Science and Data Engineering.
              I turn complex data into meaningful insights, develop AI-driven
              solutions, and build intuitive web applications.
            </p>
            <p className="mt-[12px] max-w-[600px] text-[15px] leading-[1.8] text-[#9FA0B4]">
              From Power BI dashboards to intelligent tools and full-stack experiences,
              my work connects technology with real business needs.
            </p>

            <div className="mt-[30px] flex flex-wrap gap-[11px]">
              <a
                href="#projects"
                className="inline-flex min-h-[50px] items-center justify-center gap-[12px] rounded-[12px] bg-[#7950F2] px-[21px] py-[12px] text-[14px] font-bold text-white shadow-[0_12px_35px_rgba(121,80,242,0.23)] transition duration-200 hover:-translate-y-[2px] hover:bg-[#8C68FA] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#CCB9FF]"
              >
                Explore my projects <ArrowIcon />
              </a>
              <a
                href="/PDF/CV_INANE_SALMA.pdf"
                download="Salma_Inane_CV.pdf"
                className="inline-flex min-h-[50px] items-center justify-center gap-[11px] rounded-[12px] border border-white/[0.20] bg-white/[0.055] px-[21px] py-[12px] text-[14px] font-bold text-white transition duration-200 hover:border-[#AD92F6] hover:bg-white/[0.11] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#CCB9FF]"
              >
                <DownloadIcon /> Download CV
              </a>
              <a
                href="#contact"
                className="inline-flex min-h-[50px] items-center justify-center px-[11px] text-[14px] font-semibold text-[#D6C6FF] underline decoration-[#B9A0FF]/60 underline-offset-[5px] transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#CCB9FF]"
              >
                Contact me
              </a>
            </div>

            <div className="mt-[33px] flex flex-wrap items-center gap-x-[18px] gap-y-[10px] border-t border-white/[0.11] pt-[20px]">
              <div className="inline-flex items-center gap-[9px] text-[12px] font-semibold text-[#E2D7FF]">
                <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#7950F2]/[0.22] text-[#C6B4FF]">✓</span>
                Microsoft Power BI Certified · PL-300
              </div>
              <span className="text-[12px] text-[#9194A8]">Computer Science & Data Engineering</span>
            </div>
          </motion.div>

          {/* Partie droite : trois expertises, sans sphère de logos aléatoires */}
          <motion.aside
            aria-label="Areas of expertise"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="relative w-full min-w-0 overflow-hidden rounded-[24px] border border-[#9F85E9]/[0.20] bg-[#17151F]/[0.95] p-[23px] shadow-[0_28px_90px_rgba(0,0,0,0.38)] sm:p-[28px]"
          >
            <div aria-hidden="true" className="pointer-events-none absolute -right-[80px] -top-[100px] h-[300px] w-[300px] rounded-full bg-[#7950F2]/[0.14] blur-[85px]" />
            <div className="relative">
              <div className="flex items-start justify-between gap-[14px]">
                <div>
                  <span className="text-[11px] font-bold uppercase tracking-[1.9px] text-[#BDA6FF]">What I build</span>
                  <h3 className="mt-[9px] text-[23px] font-bold leading-[1.3] tracking-[-0.4px] text-white sm:text-[26px]">
                    From insight to impact.
                  </h3>
                </div>
                <span className="rounded-[9px] border border-white/[0.09] bg-white/[0.05] px-[11px] py-[8px] text-[11px] font-bold text-[#AAA2BD]">01 — 03</span>
              </div>

              <div className="mt-[24px] space-y-[11px]">
                {expertise.map((item) => (
                  <div
                    key={item.number}
                    className="group rounded-[15px] border border-white/[0.09] bg-white/[0.035] p-[16px] transition duration-200 hover:border-[#9D7FF8]/[0.45] hover:bg-white/[0.06]"
                  >
                    <div className="flex items-start gap-[14px]">
                      <span
                        className="inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-[12px] border border-white/[0.08] bg-[#31283F]"
                        style={{ color: item.color }}
                      >
                        <DomainIcon type={item.icon} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-[12px]">
                          <h4 className="text-[15px] font-bold leading-[1.4] text-[#F4F0FF]">{item.title}</h4>
                          <span className="text-[11px] font-bold text-[#8D829F]">{item.number}</span>
                        </div>
                        <p className="mt-[5px] text-[12px] leading-[1.7] text-[#A8A5BA]">{item.description}</p>
                        <ul className="mt-[12px] flex flex-wrap gap-[7px]">
                          {item.technologies.map((tech) => (
                            <li key={tech} className="rounded-[6px] border border-[#9C8BB7]/[0.20] bg-[#9B7DDE]/[0.09] px-[8px] py-[5px] text-[11px] font-semibold text-[#CDBDEB]">
                              {tech}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-[21px] flex items-center gap-[9px] border-t border-white/[0.1] pt-[17px] text-[12px] text-[#B1A9C3]">
                <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-[#B49CFF]" />
                Explore real projects and technical demonstrations below.
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
